import React from 'react';
import { Box, Container, Typography, Grid, Link as MuiLink, useTheme, useMediaQuery } from '@mui/material';
import { Link } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import EmailIcon from '@mui/icons-material/Email';

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#f9fafb',
        color: '#111827',
        py: { xs: 3, sm: 3.5, md: 4 },
        px: { xs: 1.5, sm: 2, md: 0 },
        mt: 'auto',
        borderTop: '1px solid #e5e7eb',
      }}
    >
      <Container 
        maxWidth="lg"
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Grid container spacing={{ xs: 3, sm: 3, md: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              variant={isMobile ? 'h6' : isTablet ? 'h6' : 'h5'}
              sx={{
                fontWeight: 700,
                color: '#f97316',
                mb: { xs: 1.5, sm: 1.75, md: 2 },
                textAlign: { xs: 'center', sm: 'left', md: 'left' },
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
              }}
            >
              GBCN Foundation
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{
                textAlign: { xs: 'center', sm: 'left', md: 'left' },
                fontSize: { xs: '0.813rem', sm: '0.875rem', md: '0.875rem' },
                lineHeight: { xs: 1.7, sm: 1.6, md: 1.6 },
                mb: { xs: 1.5, sm: 0 },
              }}
            >
              Committed to the upliftment of education, preservation of Indian Sanskriti & culture, 
              women empowerment, youth empowerment, and student mentorship.
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mt: { xs: 1.5, sm: 1.75, md: 2 },
                textAlign: { xs: 'center', sm: 'left', md: 'left' },
                fontSize: { xs: '0.75rem', sm: '0.813rem', md: '0.875rem' },
                lineHeight: { xs: 1.7, sm: 1.6, md: 1.6 },
              }}
            >
              GBCN Foundation is a Section 8 Company registered under the Companies Act, 2013. All donations are utilized solely for charitable objectives in accordance with applicable laws. Eligible donations qualify for tax exemption under Section 80G of the Income Tax Act, subject to statutory conditions.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography 
              variant={isMobile ? 'subtitle1' : 'h6'} 
              gutterBottom 
              sx={{ 
                color: '#111827',
                fontWeight: 600,
                mb: { xs: 1.5, sm: 1.75, md: 2 },
                textAlign: { xs: 'center', sm: 'left', md: 'left' },
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                mt: { xs: 2, sm: 0 },
              }}
            >
              Quick Links
            </Typography>
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: { xs: 0.875, sm: 1, md: 1 },
                alignItems: { xs: 'center', sm: 'flex-start', md: 'flex-start' },
              }}
            >
              <MuiLink
                component={Link}
                to="/about"
                color="text.secondary"
                sx={{ 
                  textDecoration: 'none', 
                  fontSize: { xs: '0.875rem', sm: '0.875rem', md: '0.875rem' },
                  py: { xs: 0.5, sm: 0 },
                  minHeight: { xs: '44px', sm: 'auto' },
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': { color: '#f97316' },
                  transition: 'color 0.2s',
                }}
              >
                About Us
              </MuiLink>
              <MuiLink
                component={Link}
                to="/activities"
                color="text.secondary"
                sx={{ 
                  textDecoration: 'none', 
                  fontSize: { xs: '0.875rem', sm: '0.875rem', md: '0.875rem' },
                  py: { xs: 0.5, sm: 0 },
                  minHeight: { xs: '44px', sm: 'auto' },
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': { color: '#f97316' },
                  transition: 'color 0.2s',
                }}
              >
                Activities
              </MuiLink>
              <MuiLink
                component={Link}
                to="/events"
                color="text.secondary"
                sx={{ 
                  textDecoration: 'none', 
                  fontSize: { xs: '0.875rem', sm: '0.875rem', md: '0.875rem' },
                  py: { xs: 0.5, sm: 0 },
                  minHeight: { xs: '44px', sm: 'auto' },
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': { color: '#f97316' },
                  transition: 'color 0.2s',
                }}
              >
                Events
              </MuiLink>
              <MuiLink
                component={Link}
                to="/donate"
                color="text.secondary"
                sx={{ 
                  textDecoration: 'none', 
                  fontSize: { xs: '0.875rem', sm: '0.875rem', md: '0.875rem' },
                  py: { xs: 0.5, sm: 0 },
                  minHeight: { xs: '44px', sm: 'auto' },
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': { color: '#f97316' },
                  transition: 'color 0.2s',
                }}
              >
                Donate
              </MuiLink>
              <MuiLink
                component={Link}
                to="/downloads"
                color="text.secondary"
                sx={{ 
                  textDecoration: 'none', 
                  fontSize: { xs: '0.875rem', sm: '0.875rem', md: '0.875rem' },
                  py: { xs: 0.5, sm: 0 },
                  minHeight: { xs: '44px', sm: 'auto' },
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': { color: '#f97316' },
                  transition: 'color 0.2s',
                }}
              >
                Downloads
              </MuiLink>
              <MuiLink
                component={Link}
                to="/legal"
                color="text.secondary"
                sx={{ 
                  textDecoration: 'none', 
                  fontSize: { xs: '0.875rem', sm: '0.875rem', md: '0.875rem' },
                  py: { xs: 0.5, sm: 0 },
                  minHeight: { xs: '44px', sm: 'auto' },
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': { color: '#f97316' },
                  transition: 'color 0.2s',
                }}
              >
                Legal & Bank
              </MuiLink>
              <MuiLink
                component={Link}
                to="/contact"
                color="text.secondary"
                sx={{ 
                  textDecoration: 'none', 
                  fontSize: { xs: '0.875rem', sm: '0.875rem', md: '0.875rem' },
                  py: { xs: 0.5, sm: 0 },
                  minHeight: { xs: '44px', sm: 'auto' },
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': { color: '#f97316' },
                  transition: 'color 0.2s',
                }}
              >
                Contact
              </MuiLink>
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <Typography 
              variant={isMobile ? 'subtitle1' : 'h6'} 
              gutterBottom 
              sx={{ 
                color: '#111827',
                fontWeight: 600,
                mb: { xs: 1.5, sm: 1.75, md: 2 },
                textAlign: { xs: 'center', sm: 'left', md: 'left' },
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                mt: { xs: 2, sm: 0 },
              }}
            >
              Connect With Us
            </Typography>
            <Box 
              sx={{ 
                display: 'flex', 
                gap: { xs: 2.5, sm: 2.5, md: 2.5 },
                mt: { xs: 1, sm: 1, md: 1 },
                mb: { xs: 1, sm: 0 },
                justifyContent: { xs: 'center', sm: 'flex-start', md: 'flex-start' },
              }}
            >
              <MuiLink
                href="#"
                color="text.secondary"
                sx={{ 
                  transition: 'color 0.2s, transform 0.2s',
                  p: { xs: 1, sm: 0.5 },
                  minWidth: { xs: '44px', sm: 'auto' },
                  minHeight: { xs: '44px', sm: 'auto' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    color: '#f97316',
                    transform: 'scale(1.1)',
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                }}
              >
                <FacebookIcon sx={{ fontSize: { xs: 30, sm: 32, md: 32 } }} />
              </MuiLink>
              <MuiLink
                href="#"
                color="text.secondary"
                sx={{ 
                  transition: 'color 0.2s, transform 0.2s',
                  p: { xs: 1, sm: 0.5 },
                  minWidth: { xs: '44px', sm: 'auto' },
                  minHeight: { xs: '44px', sm: 'auto' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    color: '#f97316',
                    transform: 'scale(1.1)',
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                }}
              >
                <TwitterIcon sx={{ fontSize: { xs: 30, sm: 32, md: 32 } }} />
              </MuiLink>
              <MuiLink
                href="#"
                color="text.secondary"
                sx={{ 
                  transition: 'color 0.2s, transform 0.2s',
                  p: { xs: 1, sm: 0.5 },
                  minWidth: { xs: '44px', sm: 'auto' },
                  minHeight: { xs: '44px', sm: 'auto' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    color: '#f97316',
                    transform: 'scale(1.1)',
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                }}
              >
                <InstagramIcon sx={{ fontSize: { xs: 30, sm: 32, md: 32 } }} />
              </MuiLink>
              <MuiLink
                href="mailto:gbcnfoundation@gmail.com"
                color="text.secondary"
                sx={{ 
                  transition: 'color 0.2s, transform 0.2s',
                  p: { xs: 1, sm: 0.5 },
                  minWidth: { xs: '44px', sm: 'auto' },
                  minHeight: { xs: '44px', sm: 'auto' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    color: '#f97316',
                    transform: 'scale(1.1)',
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                }}
              >
                <EmailIcon sx={{ fontSize: { xs: 30, sm: 32, md: 32 } }} />
              </MuiLink>
            </Box>
          </Grid>
        </Grid>
        <Box
          sx={{
            borderTop: '1px solid #e5e7eb',
            mt: { xs: 3, sm: 3.5, md: 4 },
            pt: { xs: 2, sm: 2.5, md: 3 },
            pb: { xs: 1, sm: 0 },
            textAlign: 'center',
          }}
        >
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.813rem', md: '0.875rem' },
              lineHeight: { xs: 1.6, sm: 1.5, md: 1.5 },
              px: { xs: 1, sm: 0 },
            }}
          >
            © {new Date().getFullYear()} GBCN Foundation. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
